import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthApiError, authRequest } from '../../core/api/authApiClient';

export type ForgotPw2Record = {
  id: string;
  partner?: string;
  user_email: string;
  /** GET 응답에 없을 수 있음 — 이 경우 링크 토큰을 put 시 사용 */
  secureToken?: string;
  valid_at?: string;
};

export type PasswordResetPhase = 'loading' | 'invalid' | 'ready' | 'success';

function isForgotPwRecord(x: unknown): x is ForgotPw2Record {
  if (!x || typeof x !== 'object') return false;
  const r = x as Record<string, unknown>;
  return typeof r.id === 'string' && typeof r.user_email === 'string';
}

function extractRecordsFromSearchBody(body: unknown): ForgotPw2Record[] {
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  if (Array.isArray(b.data)) {
    return (b.data as unknown[]).filter(isForgotPwRecord);
  }
  const inner = b.result_message;
  if (inner && typeof inner === 'object') {
    const rm = inner as Record<string, unknown>;
    if (Array.isArray(rm.data)) {
      return (rm.data as unknown[]).filter(isForgotPwRecord);
    }
  }
  return [];
}

function extractRecordFromGetBody(body: unknown): ForgotPw2Record | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (isForgotPwRecord(b)) return b;
  const data = b.data;
  if (isForgotPwRecord(data)) return data;
  const inner = b.result_message;
  if (inner && typeof inner === 'object' && isForgotPwRecord(inner)) return inner;
  return null;
}

function isRecordExpired(validAt?: string): boolean {
  if (!validAt?.trim()) return false;
  const t = Date.parse(validAt);
  if (Number.isNaN(t)) return false;
  return t < Date.now();
}

function isPutSuccess(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  const sc = b.statusCode;
  const rc = b.result_Code ?? b.resultCode;
  const scOk = sc === 200 || sc === '200';
  const rcOk = rc === 0 || rc === '0' || rc === 200 || rc === '200';
  return Boolean(scOk && rcOk);
}

function messageFromUnknownBody(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  const m = b.result_msg ?? b.message ?? b.resultMessage;
  return typeof m === 'string' ? m : undefined;
}

const defaultPartner = () => (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF';

export function usePasswordReset(token: string) {
  const { t } = useTranslation('translation');
  const [phase, setPhase] = useState<PasswordResetPhase>('loading');
  const [record, setRecord] = useState<ForgotPw2Record | null>(null);
  const [password, setPasswordState] = useState('');
  const [confirmPassword, setConfirmPasswordState] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordMismatchError = useMemo(() => {
    if (!password || !confirmPassword) return undefined;
    if (password !== confirmPassword) return t('page.passwordReset.errors.passwordMismatch');
    return undefined;
  }, [confirmPassword, password, t]);

  const isSubmitDisabled =
    isSubmitting ||
    phase !== 'ready' ||
    !record ||
    !password ||
    !confirmPassword ||
    Boolean(passwordMismatchError);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const tkn = token?.trim();
      if (!tkn) {
        setPhase('invalid');
        return;
      }
      setPhase('loading');
      setRecord(null);

      try {
        let found: ForgotPw2Record | null = null;
        try {
          const getBody = await authRequest<unknown>('/forgotpw2/get', {
            method: 'POST',
            body: { id: tkn },
          });
          found = extractRecordFromGetBody(getBody);
        } catch {
          found = null;
        }
        if (!found) {
          const searchBody = await authRequest<unknown>('/forgotpw2/m/sch', {
            method: 'POST',
            body: {
              limit: 1,
              params: [{ fld: 'secureToken', val: tkn, op: 'eq' }],
            },
          });
          found = extractRecordsFromSearchBody(searchBody)[0] ?? null;
        }
        if (cancelled) return;
        if (!found || isRecordExpired(found.valid_at)) {
          setPhase('invalid');
          return;
        }
        setRecord(found);
        setPhase('ready');
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setPassword = useCallback((value: string) => {
    setPasswordState(value);
  }, []);

  const setConfirmPassword = useCallback((value: string) => {
    setConfirmPasswordState(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!record || isSubmitDisabled) return;
      if (password !== confirmPassword) return;

      const partner = record.partner?.trim() || defaultPartner();

      try {
        setIsSubmitting(true);

        const secureToken = record.secureToken?.trim() || token.trim();
        const response = await authRequest<unknown>('/forgotpw2/put', {
          method: 'POST',
          body: {
            id: record.id,
            partner,
            user_email: record.user_email,
            secureToken,
            password,
          },
        });
        if (isPutSuccess(response)) {
          setPhase('success');
          return;
        }
        window.alert(messageFromUnknownBody(response) ?? t('page.passwordReset.errors.putDefault'));
      } catch (error) {
        if (error instanceof AuthApiError) {
          const msg = messageFromUnknownBody(error.body);
          window.alert(msg ?? t('page.passwordReset.errors.putDefault'));
          return;
        }
        window.alert(t('page.passwordReset.errors.putDefault'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [confirmPassword, isSubmitDisabled, password, record, t, token],
  );

  return {
    phase,
    record,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordMismatchError,
    isSubmitting,
    isSubmitDisabled,
    handleSubmit,
  };
}
